import { useState, useRef, useCallback, Fragment } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useDebounceCallback, useDocumentTitle, useTimeout } from "usehooks-ts";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Loader2,
  Trash2,
  ListChecks,
  CheckSquare,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { MarkdownEditor } from "@/components/markdown-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CompactEntityCard } from "@/components/CompactEntityCard";
import { StructuredInstructions } from "@/components/StructuredInstructions";
import { noteQueryOptions } from "@/queries/notes";
import {
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useCreateNote,
} from "@/mutations/notes";
import { useCreateNoteList, useAddNoteToList } from "@/mutations/noteLists";
import { noteListQueryOptions } from "@/queries/noteLists";
import * as notesApi from "@/lib/api/notes";
import { cn } from "@/utils";
import type { EntityTypes } from "db/types";

type NoteEntityWithEntity = EntityTypes.SerializedNoteEntityWithEntity;

type ChecklistItem = {
  id: string;
  content: string;
  completed: boolean;
};

type NoteListRelation = {
  id: string;
  noteList: { id: string; name: string };
};

const noteDetailSchema = z.object({
  title: z.string(),
  content: z.string(),
  type: z.string(),
});

type NoteDetailFormValues = z.infer<typeof noteDetailSchema>;

export const Route = createFileRoute("/notes/$noteId")({
  component: NoteDetailPage,
});

function NoteDetailPage() {
  const { noteId } = Route.useParams();
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: note,
    isLoading,
    error,
  } = useQuery({
    ...noteQueryOptions(noteId),
    retry: false,
  });

  const updateMutation = useUpdateNoteMutation();
  const deleteMutation = useDeleteNoteMutation();
  const createNoteListMutation = useCreateNoteList();
  const addNoteToListMutation = useAddNoteToList();
  const createNoteMutation = useCreateNote();

  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const checklistInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<NoteDetailFormValues>({
    resolver: zodResolver(noteDetailSchema),
    values: {
      title: note?.title || "",
      content: note?.content || "",
      type: note?.type || "todo",
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const title = form.watch("title");
  const noteType = form.watch("type");

  useDocumentTitle(note ? `${note.title || "Untitled"} | Notes` : "Loading...");

  const resizeTitleTextarea = useCallback(() => {
    const textarea = titleRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  useTimeout(resizeTitleTextarea, 100);

  const debouncedSave = useDebounceCallback((field: string, value: string) => {
    updateMutation.mutate({
      noteId,
      [field]: value,
    });
  }, 500);

  const handleTitleChange = (value: string) => {
    form.setValue("title", value, { shouldDirty: true });
    debouncedSave("title", value);
    setTimeout(resizeTitleTextarea, 0);
  };

  const handleContentChange = (value: string) => {
    form.setValue("content", value, { shouldDirty: true });
    debouncedSave("content", value);
  };

  const handleTypeChange = (value: string) => {
    form.setValue("type", value, { shouldDirty: true });
    updateMutation.mutate({ noteId, type: value });
  };

  const handleDateChange = (date: Date | undefined) => {
    updateMutation.mutate({ noteId, dueDate: date || null });
  };

  const deleteEntitiesMutation = useMutation({
    mutationFn: () => notesApi.deleteNoteEntities(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["noteEntities", noteId] });
      queryClient.invalidateQueries({ queryKey: ["note", noteId] });
    },
  });

  const regenerateEntitiesMutation = useMutation({
    mutationFn: () => notesApi.regenerateNoteEntities(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["noteEntities", noteId] });
      queryClient.invalidateQueries({ queryKey: ["note", noteId] });
    },
  });

  const handleRegenerateEntities = async () => {
    try {
      await deleteEntitiesMutation.mutateAsync();
      await regenerateEntitiesMutation.mutateAsync();
    } catch (error) {
      console.error("Failed to regenerate entities:", error);
    }
  };

  const handleAddChecklistItem = (itemContent: string) => {
    if (!itemContent.trim()) return;

    setChecklistItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        content: itemContent.trim(),
        completed: false,
      },
    ]);
  };

  const handleChecklistInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const input = e.currentTarget;
      handleAddChecklistItem(input.value);
      input.value = "";
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      const input = e.currentTarget;
      handleAddChecklistItem(input.value);
      handleCreateListFromChecklist();
    }
    if (
      e.key === "Backspace" &&
      e.currentTarget.value === "" &&
      checklistItems.length > 0
    ) {
      e.preventDefault();
      const lastItem = checklistItems[checklistItems.length - 1];
      setChecklistItems((prev) => prev.slice(0, -1));
      e.currentTarget.value = lastItem.content;
    }
  };

  const handleToggleChecklist = () => {
    if (checklistItems.length === 0) {
      setChecklistItems([
        { id: crypto.randomUUID(), content: "", completed: false },
      ]);
      setTimeout(() => checklistInputRef.current?.focus(), 100);
    } else {
      setChecklistItems([]);
    }
  };

  const handleCreateListFromChecklist = async () => {
    const validItems = checklistItems.filter((item) => item.content.trim());
    if (validItems.length === 0) return;

    setIsCreatingList(true);
    try {
      const listName = title.trim() || `${validItems[0].content} list`;

      const list = await createNoteListMutation.mutateAsync({
        name: listName,
        kind: "LIST",
        schema: { properties: {} },
      });

      await addNoteToListMutation.mutateAsync({
        listId: list.id,
        noteId: noteId,
      });

      for (const item of validItems) {
        const newNote = await createNoteMutation.mutateAsync({
          title: item.content,
          content: "",
          type: "todo",
        });

        await addNoteToListMutation.mutateAsync({
          listId: list.id,
          noteId: newNote.id,
        });
      }

      await queryClient.prefetchQuery(noteListQueryOptions(list.id));

      setChecklistItems([]);
      navigate({ to: "/lists/$listId", params: { listId: list.id } });
    } catch (error) {
      console.error("Failed to create list:", error);
    } finally {
      setIsCreatingList(false);
    }
  };

  const handleDelete = () => {
    const parentListId = noteLists?.[0]?.noteList?.id;
    deleteMutation.mutate(noteId, {
      onSuccess: () => {
        if (parentListId) {
          navigate({ to: "/lists/$listId", params: { listId: parentListId } });
        } else {
          navigate({ to: "/" });
        }
      },
    });
  };

  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">Sign in to view this note</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">Not Found</h2>
          <p className="text-muted-foreground">
            {error instanceof Error
              ? error.message
              : "This note doesn't exist or you don't have access to it."}
          </p>
          <Button asChild>
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  const metadata = note.noteMetadata || null;
  const noteInstructions = note.noteInstructions;
  const instructions = noteInstructions?.content
    ? (() => {
        try {
          return JSON.parse(noteInstructions.content);
        } catch {
          return noteInstructions.content;
        }
      })()
    : note.instructions || "";

  const noteEntities = (note.noteEntities ||
    []) as unknown as NoteEntityWithEntity[];

  const noteLists = (note as unknown as { noteLists?: NoteListRelation[] })
    .noteLists;
  const isAlreadyInList = (noteLists?.length ?? 0) > 0;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-2 p-4 md:p-6 pb-4 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" asChild className="h-8 w-8">
            {noteLists && noteLists.length > 0 ? (
              <Link
                to="/lists/$listId"
                params={{ listId: noteLists[0].noteList.id }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
            ) : (
              <Link to="/">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            )}
          </Button>
          <span className="text-sm text-muted-foreground">
            {noteLists && noteLists.length > 0
              ? noteLists[0].noteList.name
              : "Home"}
          </span>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-6 overflow-y-auto">
        <div className="max-w-3xl mx-auto">
          <Form {...form}>
            <div className="space-y-6">
              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          ref={titleRef}
                          value={field.value}
                          onChange={(e) => handleTitleChange(e.target.value)}
                          placeholder="Note title..."
                          rows={1}
                          className="text-2xl font-semibold border-none p-0 min-h-[2rem] resize-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent overflow-hidden"
                          autoFocus
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="note-completed"
                      checked={note.status === "COMPLETED"}
                      onCheckedChange={(checked) => {
                        updateMutation.mutate({
                          noteId,
                          status: checked ? "COMPLETED" : "NOT_STARTED",
                        });
                      }}
                      className="h-5 w-5"
                    />
                    <label
                      htmlFor="note-completed"
                      className="text-sm text-muted-foreground cursor-pointer"
                    >
                      {note.status === "COMPLETED"
                        ? "Completed"
                        : "Mark complete"}
                    </label>
                  </div>
                  <Badge variant="secondary" className="capitalize">
                    {noteType}
                  </Badge>
                  {note.dueDate && (
                    <Badge variant="outline">
                      Due: {new Date(note.dueDate).toLocaleDateString()}
                    </Badge>
                  )}
                  {!isAlreadyInList && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleToggleChecklist}
                      className={cn(
                        "h-7 px-2 gap-1.5",
                        checklistItems.length > 0 &&
                          "bg-primary/10 text-primary",
                      )}
                      title={
                        checklistItems.length > 0
                          ? "Remove checklist"
                          : "Add checklist items"
                      }
                    >
                      <CheckSquare className="h-4 w-4" />
                      <span className="text-xs">
                        {checklistItems.length > 0 ? "Checklist" : "Add list"}
                      </span>
                    </Button>
                  )}
                </div>
                {noteLists && noteLists.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                    <ListChecks className="h-4 w-4 flex-shrink-0" />
                    {noteLists.map((nln, index) => (
                      <Fragment key={nln.id}>
                        {index > 0 && (
                          <span className="text-muted-foreground/50">•</span>
                        )}
                        <Link
                          to="/lists/$listId"
                          params={{ listId: nln.noteList.id }}
                          className="hover:text-foreground hover:underline underline-offset-2 transition-colors"
                        >
                          {nln.noteList.name}
                        </Link>
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>

              {checklistItems.length === 0 ? (
                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <MarkdownEditor
                          key={note.id}
                          content={field.value}
                          onChange={handleContentChange}
                          placeholder="Add content..."
                          className="min-h-[200px]"
                          editable={true}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ) : (
                <div className="space-y-2">
                  <AnimatePresence mode="popLayout">
                    {checklistItems.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 25,
                        }}
                        className="group flex items-center gap-2 p-2 rounded-md border bg-muted/30 hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={item.completed}
                          onCheckedChange={(checked) => {
                            setChecklistItems((prev) =>
                              prev.map((ci, i) =>
                                i === index
                                  ? { ...ci, completed: !!checked }
                                  : ci,
                              ),
                            );
                          }}
                        />
                        <Input
                          value={item.content}
                          onChange={(e) => {
                            setChecklistItems((prev) =>
                              prev.map((ci, i) =>
                                i === index
                                  ? { ...ci, content: e.target.value }
                                  : ci,
                              ),
                            );
                          }}
                          placeholder="Item..."
                          className="flex-1 border-0 bg-transparent focus-visible:ring-0 px-2 h-8"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newItems = checklistItems.filter(
                              (_, i) => i !== index,
                            );
                            setChecklistItems(newItems);
                          }}
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  <div className="flex items-center gap-2 p-2 rounded-md border border-dashed border-border/50 hover:border-border transition-colors">
                    <Plus className="h-4 w-4 text-muted-foreground" />
                    <Input
                      ref={checklistInputRef}
                      onKeyDown={handleChecklistInputKeyDown}
                      placeholder="Add item..."
                      aria-label="Add checklist item"
                      className="flex-1 border-0 bg-transparent focus-visible:ring-0 px-0 h-8 placeholder:text-muted-foreground/60"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-muted-foreground">
                      {checklistItems.filter((i) => i.content.trim()).length}{" "}
                      item
                      {checklistItems.filter((i) => i.content.trim()).length !==
                      1
                        ? "s"
                        : ""}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateListFromChecklist}
                      disabled={
                        isCreatingList ||
                        checklistItems.filter((i) => i.content.trim())
                          .length === 0
                      }
                      className="h-8"
                    >
                      {isCreatingList ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          Creating...
                        </>
                      ) : (
                        "Create list"
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <DatePicker
                  date={note.dueDate ? new Date(note.dueDate) : undefined}
                  onDateChange={handleDateChange}
                />
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        value={field.value}
                        onValueChange={handleTypeChange}
                      >
                        <FormControl>
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="todo">Todo</SelectItem>
                          <SelectItem value="note">Note</SelectItem>
                          <SelectItem value="link">Link</SelectItem>
                          <SelectItem value="bookmark">Bookmark</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              {noteEntities.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Entities ({noteEntities.length})
                    </h3>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteEntitiesMutation.mutate()}
                        disabled={deleteEntitiesMutation.isPending}
                        className="h-7 px-2 text-xs"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Clear
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRegenerateEntities}
                        disabled={
                          regenerateEntitiesMutation.isPending ||
                          deleteEntitiesMutation.isPending
                        }
                        className="h-7 px-2 text-xs"
                      >
                        <RefreshCw
                          className={`h-3 w-3 mr-1 ${
                            regenerateEntitiesMutation.isPending ||
                            deleteEntitiesMutation.isPending
                              ? "animate-spin"
                              : ""
                          }`}
                        />
                        Regenerate
                      </Button>
                    </div>
                  </div>

                  <div
                    className="overflow-y-auto border border-border rounded-lg p-2 space-y-1.5 min-h-0"
                    role="region"
                    aria-label="Note entities list"
                  >
                    {noteEntities.map((ne) => (
                      <CompactEntityCard key={ne.id} noteEntity={ne} />
                    ))}
                  </div>
                </div>
              )}

              {instructions && (
                <StructuredInstructions
                  instructions={instructions}
                  status={noteInstructions?.status}
                  generatedAt={noteInstructions?.generatedAt ?? undefined}
                  errorMessage={noteInstructions?.errorMessage || undefined}
                />
              )}

              {metadata && (
                <div className="p-4 border border-border rounded-lg bg-muted/30">
                  {metadata.imageUrl && (
                    <img
                      src={metadata.imageUrl}
                      alt="Preview"
                      className="w-full h-auto max-h-64 object-cover rounded-md mb-3"
                    />
                  )}
                  {metadata.sourceUrl && (
                    <a
                      href={metadata.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm block mb-2"
                    >
                      {metadata.sourceUrl}
                    </a>
                  )}
                  {metadata.siteName && (
                    <p className="text-xs text-muted-foreground">
                      {metadata.siteName}
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-xs text-muted-foreground">
                  {note.createdAt && (
                    <span>
                      Created{" "}
                      {new Date(note.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  )}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete note?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete "{title || "this note"}".
                        This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}
